import { request } from './apiClient';
import { getSessionClientId } from './authSession';

// Lê o primeiro valor não vazio dentre as chaves informadas (resiliente a snake_case/camelCase).
function resolveFirstString(item, keys) {
  for (const key of keys) {
    const value = item?.[key];
    if (value !== undefined && value !== null && String(value).trim()) {
      return String(value).trim();
    }
  }
  return '';
}

// Normaliza um número de nota fiscal para comparação de duplicidade: '', '0' e nulos viram '' (sem NF).
function normalizeNotaFiscalValue(value) {
  const asString = String(value ?? '').trim();
  return asString === '' || asString === '0' ? '' : asString;
}

// Mantem a tabela resiliente quando o backend alterna entre snake_case e camelCase.
export function normalizeCaminhaoRegistro(item) {
  const notaFiscalPrincipal = resolveFirstString(item, [
    'notaFiscal',
    'nota_fiscal',
    'numrNotaFiscalPrincipal',
    'numr_nota_fiscal_principal',
  ]);
  const notaFiscalSecundaria = resolveFirstString(item, [
    'numrNotaFiscalSecundaria',
    'numr_nota_fiscal_secundaria',
  ]);
  const flagSemNotaFiscal = resolveFirstString(item, ['flagSemNotaFiscal', 'flag_sem_nota_fiscal']);

  const notaFiscal = [notaFiscalPrincipal, notaFiscalSecundaria]
    .map((value) => String(value || '').trim())
    .find((value) => value && value !== '0')
    || (String(flagSemNotaFiscal).trim() === '1' ? 'Sem nota fiscal' : '-');

  return {
    id: String(item.id || item.id_caminhao || item.idCaminhao || `truck-${Date.now()}-${Math.random()}`),
    idConcretagem: String(item.concretagemRealizada?.idConcretagemRealizada || item.id_concretagem_realizada || item.idConcretagemRealizada || item.concretagemRealizadaId || ''),
    obra: item.concretagemRealizada?.obra?.nomeObra || item.obra || item.nome_obra || item.nomeObra || '',
    empresa: item.concretagemRealizada?.obra?.empresa?.razaoSocial || item.empresa || item.nome_empresa || item.nomeEmpresa || item.razao_social || item.obra?.empresa?.razaoSocial || '',
    placaCaminhao: item.placaCaminhao || item.placa_caminhao || item.placa || '',
    notaFiscal,
    // Valores usados para validar duplicidade dentro da mesma concretagem.
    nfPrincipal: normalizeNotaFiscalValue(notaFiscalPrincipal),
    nfSecundaria: normalizeNotaFiscalValue(notaFiscalSecundaria),
    lacre: resolveFirstString(item, ['numrLacreCaminhao', 'numr_lacre_caminhao', 'lacre']),
    dataConcretagem: item.concretagemRealizada?.dataConcretagem || item.dataConcretagem || item.data_concretagem || item.data || '',
    quantidadeCp: Number(item.quantidadeCp || item.quantidade_cp || item.qtd_cp || item.qtd_total_cp || item.qtdeCpCaminhao || 0),
    status: item.status || item.situacao || 'Concluido',
  };
}

// Calcula a data de ruptura dos CPs a partir da data base da concretagem.
function addDays(dateIso, days) {
  if (!dateIso || !days) return '';
  const base = new Date(dateIso);
  if (Number.isNaN(base.getTime())) return '';
  const next = new Date(base);
  next.setDate(base.getDate() + Number(days));
  return next.toISOString().split('T')[0];
}

// O backend recebe datetime completo; na UI coletamos data e hora separadas.
function combineDateAndTimeToIso(dateIso, timeValue) {
  if (!dateIso || !timeValue) return '';
  const parsed = new Date(`${dateIso}T${timeValue}`);
  if (Number.isNaN(parsed.getTime())) return '';
  // AJUSTE BACKEND AQUI: se o backend aceitar outro formato de data/hora, ajuste aqui.
  return parsed.toISOString();
}

// Converte para número, ou null se vazio/não numérico.
function parseNullableNumber(value) {
  if (value === undefined || value === null || value === '') return null;
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : null;
}

// Monta o payload de caminhão, incluindo corpos de prova e testes de slump aninhados.
export const buildCaminhaoPayload = (formData, cpRows, slumpRows) => {
  const corposProva = cpRows.map((row) => ({
    idCliente: getSessionClientId(),
    idTipoCorpoProva: parseNullableNumber(row.tipo),
    idenObraEtiqueta: String(row.etiqueta || '').trim() || null,
    idadeRuptura: parseNullableNumber(row.idadeRuptura),
    dataRuptura: addDays(formData.dataConcretagem, row.idadeRuptura) || null,
  }));

  const slumpFinalStr = slumpRows.length > 0 ? String(slumpRows[slumpRows.length - 1]?.valrSlump ?? '').trim() : '';
  const valrSlumpFinal = slumpFinalStr !== '' ? Number(slumpFinalStr) : null;

  const slump = slumpRows.map((row) => ({
    idCliente: getSessionClientId(),
    dataTeste: formData.dataConcretagem,
    valrSlump: parseNullableNumber(row.valrSlump),
    valrAdicaoAgua: parseNullableNumber(row.valrAdicaoAgua),
    valrAdicaoAditivo: parseNullableNumber(row.valrAdicaoAditivo),
    valrSlumpFinal: valrSlumpFinal,
  }));

  return {
    idCliente: getSessionClientId(),
    // Mantem o payload explicito e aderente ao contrato legado do backend.
    idConcretagemRealizada: parseNullableNumber(formData.concretagemRealizadaId),
    idEtapaObra: parseNullableNumber(formData.etapaObra),
    idTipoCimento: parseNullableNumber(formData.tipoCimento),
    idTipoConcreto: parseNullableNumber(formData.tipoConcreto),
    idConcreteira: parseNullableNumber(formData.concreteira),
    idCartaTraco: parseNullableNumber(formData.cartaTraco),
    placaCaminhao: String(formData.placaCaminhao || '').trim() || null,
    localConcretagem: String(formData.localConcretagem || '').trim() || null,
    valrClasResiCompressao: parseNullableNumber(formData.valrClasResiCompressao) ?? 0,
    valrClasResiTracaoFlexao: parseNullableNumber(formData.valrClasResiTracaoFlexao) ?? 0,
    valrClasResiTracaoIndireta: parseNullableNumber(formData.valrClasResiTracaoIndireta) ?? 0,
    valrClasResiTracaoDireta: parseNullableNumber(formData.valrClasResiTracaoDireta) ?? 0,
    valrClasTrabalhabilidade: parseNullableNumber(formData.valrClasTrabalhabiilidade) ?? 0,
    numrNotaFiscalPrincipal: parseNullableNumber(formData.numrNotaFiscalPrincipal) ?? 0,
    numrNotaFiscalSecundaria: parseNullableNumber(formData.numrNotaFiscalSecundaria) ?? 0,
    numrLacreCaminhao: String(formData.numrLacreCaminhao || '').trim() || null,
    valrVolume: parseNullableNumber(formData.valrVolume) ?? 0,
    dataSaidaUsina: combineDateAndTimeToIso(formData.dataConcretagem, formData.dataSaidaUsina) || null,
    dataChegadaUsina: combineDateAndTimeToIso(formData.dataConcretagem, formData.dataChegadaUsina) || null,
    dataChegadaObra: combineDateAndTimeToIso(formData.dataConcretagem, formData.dataChegadaObra) || null,
    dataInicioDescarga: combineDateAndTimeToIso(formData.dataConcretagem, formData.dataInicioDescarga) || null,
    dataFimDescarga: combineDateAndTimeToIso(formData.dataConcretagem, formData.dataFimDescarga) || null,
    valrTemperaturaConcreto: parseNullableNumber(formData.valrTemperaturaConcreto) ?? 0,
    valrTemperaturaAmbiente: parseNullableNumber(formData.valrTemperaturaAmbiente) ?? 0,
    flag_sem_nota_fiscal: parseNullableNumber(formData.flagSemNotaFiscal) ?? 0,
    flagContAdicaoAgua: parseNullableNumber(formData.flagContAdicaoAgua) ?? 0,
    flagIev: parseNullableNumber(formData.flagIev) ?? 0,
    observacao: String(formData.observacoes || '').trim() || null,
    qtdeCpCaminhao: parseNullableNumber(formData.quantidadeCp) ?? 0,
    corpoProvas: corposProva,
    testeSlumps: slump,
  };
};

// Lista todos os caminhões.
export const getCaminhao = async (idCliente) => {
  // AJUSTE BACKEND AQUI: endpoint de listagem de caminhões.  
  const effectiveClientId = idCliente || getSessionClientId();
  return await request(`/api/caminhao?idCliente=${effectiveClientId}`);
};

// Busca um caminhão completo por id (com CPs e slumps).
export const getCaminhaoById = async (id, idCliente) => {
  const effectiveClientId = idCliente || getSessionClientId();
  return await request(`/api/caminhao/${id}?idCliente=${effectiveClientId}`);
};

// Cria um caminhão; retorna { response, payload }.
export const createCaminhao = async (formData, cpRows, slumpRows) => {
  const payload = buildCaminhaoPayload(formData, cpRows, slumpRows);

  console.log("Payload enviado para createCaminhao:", payload);

  // AJUSTE BACKEND AQUI: endpoint de criacao de caminhão.
  const response = await request('/api/caminhao', {
    method: 'POST',
    body: JSON.stringify(payload)
  });

  // Retorna payload para facilitar rastreio de integração em debug com o backend.
  return { response, payload };
};

// Atualiza um caminhão por id; retorna { response, payload }.
export const updateCaminhao = async (id, formData, cpRows, slumpRows) => {
  const payload = buildCaminhaoPayload(formData, cpRows, slumpRows);

  // AJUSTE BACKEND AQUI: update por id do caminhão.
  const response = await request(`/api/caminhao/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  });

  // Retorna payload para facilitar rastreio de integração em debug com o backend.
  return { response, payload };
};

// Exclui um caminhão por id.
export const deleteCaminhao = async (id) => {
  // AJUSTE BACKEND AQUI: exclusao por id do caminhão.
  return await request(`/api/caminhao/${id}`, {
    method: 'DELETE'
  });
};
